import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Upload, FilePresent } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { closeDialog, addNotification } from '../../store/slices/uiSlice';
import { FirestoreService } from '../../firebase/firestoreService';
import { parseCSVToItems, ParsedItem } from '../../utils/csvUtils';
import { InventoryItem } from '../../types/models';
import { v4 as uuidv4 } from 'uuid';

const ImportDialog: React.FC = () => {
  const dispatch = useDispatch();
  const { dialog } = useSelector((state: RootState) => state.ui);
  const { organization } = useSelector((state: RootState) => state.auth);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [parsedItems, setParsedItems] = useState<ParsedItem[]>([]);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const isOpen = dialog.open && dialog.type === 'import';

  const handleClose = () => {
    dispatch(closeDialog());
    setParsedItems([]);
    setFileName('');
    setError(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setFileName(file.name);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const items = parseCSVToItems(text);
      if (items.length === 0) {
        setError('No valid items found. Make sure the CSV has a column with "name" in the header.');
        return;
      }
      setParsedItems(items);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (parsedItems.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      let imported = 0;
      for (const parsed of parsedItems) {
        const newItem: Omit<InventoryItem, 'id'> = {
          itemID: uuidv4(),
          itemName: parsed.itemName,
          itemQRFileName: '',
          checkedOut: false,
          checkOutHistory: [],
          itemGroup: parsed.itemGroup,
          lastUpdated: Date.now() / 1000,
          ...(organization?.id ? { organizationID: organization.id } : {}),
        };
        await FirestoreService.addInventoryItem(newItem);
        imported++;
      }

      dispatch(addNotification({
        message: `${imported} item(s) imported successfully`,
        type: 'success',
      }));
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to import items');
      dispatch(addNotification({ message: 'Import failed', type: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Upload color="primary" />
          <Typography variant="h6">Import Items from CSV</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {parsedItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              size="large"
              startIcon={<FilePresent />}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose CSV File
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              CSV should have columns: Item Name (required), Item Group (optional)
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                File: {fileName}
              </Typography>
              <Chip label={`${parsedItems.length} items`} size="small" color="primary" />
              <Button size="small" onClick={() => { setParsedItems([]); setFileName(''); }}>
                Change File
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Item Name</TableCell>
                    <TableCell>Group</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedItems.slice(0, 100).map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{item.itemName}</TableCell>
                      <TableCell>{item.itemGroup || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {parsedItems.length > 100 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="caption" color="text.secondary">
                          ... and {parsedItems.length - 100} more items
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={loading || parsedItems.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : <Upload />}
        >
          {loading ? 'Importing...' : `Import ${parsedItems.length} Item(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
